import { useCallback, useEffect, useRef } from "react"
import { useRouter } from "next/router"

import type { SignInResource, SignInSecondFactor } from "@clerk/shared/types"

import { CONTINUATION_PARAM } from "../utils/signinContinuation"

interface Props {
  isLoaded: boolean
  signIn: SignInResource | undefined
  invitationTicket?: string
  onResume: (factors: SignInSecondFactor[]) => void
  onStartOver: () => void
}

export function useSigninContinuation({
  isLoaded,
  signIn,
  invitationTicket,
  onResume,
  onStartOver,
}: Props) {
  const router = useRouter()
  const previousMarker = useRef<string | null | undefined>(undefined)
  const ownedAttempt = useRef<string | null>(null)

  const writeMarker = useCallback((next: string | null) => {
    const url = new URL(window.location.href)
    if (next) {
      url.searchParams.set(CONTINUATION_PARAM, next)
    } else {
      url.searchParams.delete(CONTINUATION_PARAM)
      if (url.hash.startsWith("#/")) {
        url.hash = ""
      }
    }
    if (!url.pathname.startsWith("/") || url.pathname.startsWith("//")) {
      return
    }
    // Metadata updates must not navigate over Clerk's hash-routed verification.
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    )
    previousMarker.current = next
  }, [])

  const rememberAttempt = useCallback(
    (id: string | null | undefined) => {
      if (!id) {
        return
      }
      ownedAttempt.current = id
      writeMarker(`attempt:${id}`)
    },
    [writeMarker],
  )

  const startOver = useCallback(() => {
    ownedAttempt.current = null
    onStartOver()
    writeMarker(null)
  }, [onStartOver, writeMarker])

  useEffect(() => {
    if (!router.isReady || !isLoaded || !signIn || invitationTicket) {
      return
    }
    const markers = new URLSearchParams(window.location.search).getAll(
      CONTINUATION_PARAM,
    )
    const marker = markers.length === 1 ? markers[0] : null
    const changed =
      previousMarker.current !== undefined && previousMarker.current !== marker
    previousMarker.current = marker
    const verificationLink = window.location.hash.split("?")[0] === "#/verify"
    const intended =
      marker === "oauth" ||
      (Boolean(signIn.id) && marker === `attempt:${signIn.id}`) ||
      verificationLink
    if (!intended) {
      if (
        changed ||
        (ownedAttempt.current && ownedAttempt.current !== signIn.id)
      ) {
        ownedAttempt.current = null
        onStartOver()
      }
      return
    }
    const attemptKey = signIn.id ?? "verification-link"
    if (ownedAttempt.current === attemptKey) {
      return
    }
    if (
      signIn.status !== "needs_second_factor" &&
      signIn.status !== "needs_client_trust" &&
      !verificationLink
    ) {
      return
    }
    ownedAttempt.current = attemptKey
    onResume(signIn.supportedSecondFactors ?? [])
    rememberAttempt(signIn.id)
  }, [
    router.isReady,
    isLoaded,
    signIn,
    signIn?.id,
    signIn?.status,
    signIn?.supportedSecondFactors,
    invitationTicket,
    router.asPath,
    onResume,
    onStartOver,
    rememberAttempt,
  ])

  return { rememberAttempt, startOver }
}
